import { useState, useEffect } from 'react';
import { useLanguage } from '@/context/LanguageContext';
import api from '@/utils/api';
import { toast } from 'sonner';
import { 
  CalendarCheck, Clock, Check, X, Eye, Filter, ChevronDown,
  CheckCircle, XCircle, AlertCircle, Trash2, User, Phone, Mail
} from 'lucide-react';

const STATUS_LABELS = {
  pending: 'Pendente',
  confirmed: 'Confirmado',
  completed: 'Concluído',
  cancelled: 'Cancelado',
};

const STATUS_COLORS = {
  pending: 'bg-yellow-50 text-yellow-700 border-yellow-200',
  confirmed: 'bg-green-50 text-green-700 border-green-200',
  completed: 'bg-blue-50 text-blue-700 border-blue-200',
  cancelled: 'bg-gray-100 text-gray-500 border-gray-200',
};

export default function AdminAppointments() {
  const { t } = useLanguage();
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ status: '', date: '' });
  const [selectedApt, setSelectedApt] = useState(null);
  const [updating, setUpdating] = useState(false);

  const fetchAppointments = () => {
    let url = '/appointments';
    const params = [];
    if (filter.status) params.push(`status=${filter.status}`);
    if (filter.date) params.push(`date=${filter.date}`);
    if (params.length > 0) url += '?' + params.join('&');
    
    api.get(url).then(r => setAppointments(r.data || [])).finally(() => setLoading(false));
  };

  useEffect(() => { fetchAppointments(); }, [filter]);

  const handleUpdateStatus = async (id, status) => {
    setUpdating(true);
    try {
      await api.put(`/appointments/${id}`, { status });
      toast.success(`Agendamento ${STATUS_LABELS[status].toLowerCase()}`);
      fetchAppointments();
      setSelectedApt(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao atualizar agendamento');
    } finally {
      setUpdating(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja eliminar este agendamento?')) return;
    try {
      await api.delete(`/appointments/${id}`);
      toast.success('Agendamento eliminado');
      fetchAppointments();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao eliminar');
    }
  };

  const todayCount = appointments.filter(a => a.date === new Date().toISOString().split('T')[0]).length;
  const pendingCount = appointments.filter(a => a.status === 'pending').length;

  return (
    <div data-testid="admin-appointments">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Agendamentos</h1>
          <p className="text-sm text-gray-500">{todayCount} hoje · {pendingCount} pendentes</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-6">
        <select
          value={filter.status}
          onChange={e => setFilter(f => ({ ...f, status: e.target.value }))}
          className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500/20"
        >
          <option value="">Todos os estados</option>
          {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <input
          type="date"
          value={filter.date}
          onChange={e => setFilter(f => ({ ...f, date: e.target.value }))}
          className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500/20"
        />
        {(filter.status || filter.date) && (
          <button onClick={() => setFilter({ status: '', date: '' })} className="text-sm text-gray-500 hover:text-red-600">
            Limpar filtros
          </button>
        )}
      </div>

      {/* Appointments List */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-6 space-y-3">{[...Array(5)].map((_, i) => <div key={i} className="h-16 bg-gray-100 rounded animate-pulse" />)}</div>
        ) : appointments.length === 0 ? (
          <div className="p-12 text-center text-gray-400">
            <CalendarCheck size={32} className="mx-auto mb-3 text-gray-300" />
            <p>Nenhum agendamento encontrado.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Referência</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Serviço</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase hidden md:table-cell">Cliente</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Data/Hora</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Estado</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {appointments.map(apt => (
                  <tr key={apt.id} className="hover:bg-gray-50 transition-colors" data-testid={`appointment-${apt.id}`}>
                    <td className="px-4 py-3">
                      <span className="font-mono text-sm font-medium text-[#CC0000]">{apt.reference_code}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm font-medium text-gray-900">{apt.service_name}</span>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{apt.full_name}</p>
                        <p className="text-xs text-gray-400">{apt.email}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {new Date(apt.date).toLocaleDateString('pt', { day: '2-digit', month: 'short' })}
                        </p>
                        <p className="text-xs text-gray-500">{apt.time_slot}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium border ${STATUS_COLORS[apt.status]}`}>
                        {STATUS_LABELS[apt.status]}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => setSelectedApt(apt)} className="p-1.5 text-gray-400 hover:text-[#CC0000] rounded hover:bg-red-50" title="Ver detalhes">
                          <Eye size={14} />
                        </button>
                        {apt.status === 'pending' && (
                          <>
                            <button onClick={() => handleUpdateStatus(apt.id, 'confirmed')} className="p-1.5 text-gray-400 hover:text-green-600 rounded hover:bg-green-50" title="Confirmar">
                              <Check size={14} />
                            </button>
                            <button onClick={() => handleUpdateStatus(apt.id, 'cancelled')} className="p-1.5 text-gray-400 hover:text-red-600 rounded hover:bg-red-50" title="Cancelar">
                              <X size={14} />
                            </button>
                          </>
                        )}
                        {apt.status === 'confirmed' && (
                          <button onClick={() => handleUpdateStatus(apt.id, 'completed')} className="p-1.5 text-gray-400 hover:text-blue-600 rounded hover:bg-blue-50" title="Marcar como concluído">
                            <CheckCircle size={14} />
                          </button>
                        )}
                        <button onClick={() => handleDelete(apt.id)} className="p-1.5 text-gray-400 hover:text-red-600 rounded hover:bg-red-50" title="Eliminar">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {selectedApt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="appointment-modal">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">Detalhes do Agendamento</h3>
              <button onClick={() => setSelectedApt(null)} className="text-gray-400 hover:text-gray-600">
                <X size={18} />
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                <span className="text-sm text-gray-500">Referência</span>
                <span className="font-mono font-bold text-[#CC0000]">{selectedApt.reference_code}</span>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-400 mb-1">Serviço</p>
                  <p className="text-sm font-medium">{selectedApt.service_name}</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-400 mb-1">Estado</p>
                  <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[selectedApt.status]}`}>
                    {STATUS_LABELS[selectedApt.status]}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-400 mb-1">Data</p>
                  <p className="text-sm font-medium">{new Date(selectedApt.date).toLocaleDateString('pt')}</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-400 mb-1">Hora</p>
                  <p className="text-sm font-medium">{selectedApt.time_slot}</p>
                </div>
              </div>

              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-400 mb-2">Cliente</p>
                <div className="space-y-1">
                  <p className="text-sm font-medium flex items-center gap-2"><User size={14} className="text-gray-400" />{selectedApt.full_name}</p>
                  <p className="text-sm text-gray-600 flex items-center gap-2"><Mail size={14} className="text-gray-400" />{selectedApt.email}</p>
                  <p className="text-sm text-gray-600 flex items-center gap-2"><Phone size={14} className="text-gray-400" />{selectedApt.phone}</p>
                </div>
              </div>

              {selectedApt.notes && (
                <div className="p-3 bg-amber-50 rounded-lg">
                  <p className="text-xs text-amber-600 mb-1">Notas</p>
                  <p className="text-sm text-amber-800">{selectedApt.notes}</p>
                </div>
              )}
            </div>

            <div className="flex gap-2 mt-6">
              {selectedApt.status === 'pending' && (
                <>
                  <button onClick={() => handleUpdateStatus(selectedApt.id, 'confirmed')} disabled={updating}
                    className="flex-1 py-2 bg-green-600 text-white rounded-md text-sm font-medium hover:bg-green-700 disabled:opacity-50">
                    Confirmar
                  </button>
                  <button onClick={() => handleUpdateStatus(selectedApt.id, 'cancelled')} disabled={updating}
                    className="flex-1 py-2 bg-red-600 text-white rounded-md text-sm font-medium hover:bg-red-700 disabled:opacity-50">
                    Cancelar
                  </button>
                </>
              )}
              {selectedApt.status === 'confirmed' && (
                <button onClick={() => handleUpdateStatus(selectedApt.id, 'completed')} disabled={updating}
                  className="flex-1 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
                  Marcar como Concluído
                </button>
              )}
              <button onClick={() => setSelectedApt(null)} className="flex-1 py-2 border border-gray-300 text-gray-700 rounded-md text-sm font-medium hover:bg-gray-50">
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
